import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search,
  Loader2,
  AlertCircle,
  CheckCircle,
  CheckCheck,
  Trash2,
  X,
  Handshake,
  Users,
  Wallet,
  Banknote,
  Landmark,
  Smartphone,
  ScrollText,
  ShieldCheck,
  Phone,
  Hash,
  TrendingUp,
  Percent,
  History,
  ReceiptText,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import {
  usePayeeOutstanding,
  usePayeeAccruals,
  usePayoutVouchers,
  useSettlePayouts,
  useSettlePayeesBulk,
  useRemoveOutstandingByPayees,
} from '../../services/httpServices/queries';
import { cn } from '../../lib/utils';
import { useDebounce } from '../../hooks/useDebounce';
import { usePermissions } from '../../hooks/usePermissions';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { money, payoutStatusOf, PAYOUT_PAGE_SIZE, type PayeeKind } from './payoutShared';
import { PayeeTypeBadge, PayoutStatusBadge, SourceBadge } from './payoutBadges';
import type {
  PayeeOutstandingRow,
  RevenueDistribution,
  DiagnosticReceipt,
} from '../../services/httpServices/diagnosticService';

type TypeFilter = 'ALL' | PayeeKind;

/** Selection is keyed by payee identity, since rows are per-payee aggregates. */
type PayeeRef = { payeeType: PayeeKind; payeeId: string };
const rowKey = (row: PayeeOutstandingRow) => `${row.payeeType}:${row.payeeId ?? row.payeeName}`;
const isSelectable = (row: PayeeOutstandingRow) => !!row.payeeId && row.outstandingShare > 0;

/**
 * Outstanding payables — **one ledger of referral partners**.
 *
 * Doctors, employees and outside agents are the same kind of row here;
 * `payeeType` is an attribute (and an optional filter), never a separate
 * screen. Settling a row issues an `RP-` payout voucher through the single
 * generic `billing/payouts/settle` endpoint.
 */
export function PayoutOutstanding({
  canDoctors = true,
  canReferrals = true,
}: {
  canDoctors?: boolean;
  canReferrals?: boolean;
}) {
  const bothPayees = canDoctors && canReferrals;
  const { hasPermission } = usePermissions();
  const canRemove = hasPermission('billing:settlement:delete');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>(
    bothPayees ? 'ALL' : canReferrals ? 'REFERRAL' : 'DOCTOR',
  );
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [settling, setSettling] = useState<PayeeOutstandingRow | null>(null);
  const [details, setDetails] = useState<PayeeOutstandingRow | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkSettleOpen, setBulkSettleOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const headerRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPage(1);
  }, [typeFilter, debounced]);

  const { data, isLoading, error } = usePayeeOutstanding({
    payeeType: typeFilter === 'ALL' ? undefined : typeFilter,
    search: debounced.trim() || undefined,
    page,
    limit: PAYOUT_PAGE_SIZE,
  });

  const rows = useMemo(() => data?.data ?? [], [data]);
  const totals = data?.stats?.totals;

  const settleBulk = useSettlePayeesBulk();
  const removeOutstanding = useRemoveOutstandingByPayees();

  const selectableRows = useMemo(() => rows.filter(isSelectable), [rows]);

  // Drop selected payees that scrolled off the current page or became settled.
  useEffect(() => {
    setSelectedKeys((prev) => {
      if (prev.length === 0) return prev;
      const visible = new Set(selectableRows.map(rowKey));
      const next = prev.filter((key) => visible.has(key));
      return next.length === prev.length ? prev : next;
    });
  }, [selectableRows]);

  const selectedRows = useMemo(
    () => selectableRows.filter((row) => selectedKeys.includes(rowKey(row))),
    [selectableRows, selectedKeys],
  );
  const selectedPayees: PayeeRef[] = selectedRows
    .map((row) => ({ payeeType: row.payeeType, payeeId: row.payeeId as string }));
  const selectedOutstanding = selectedRows.reduce((sum, row) => sum + row.outstandingShare, 0);

  const allSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selectedKeys.includes(rowKey(row)));
  const someSelected = selectedKeys.length > 0;
  useEffect(() => {
    if (headerRef.current) headerRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  const toggleAll = () =>
    setSelectedKeys(allSelected ? [] : selectableRows.map(rowKey));

  const toggleOne = (row: PayeeOutstandingRow) => {
    if (!isSelectable(row)) return;
    const key = rowKey(row);
    setSelectedKeys((prev) =>
      prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key],
    );
  };

  const handleBulkSettle = async ({ method, notes }: { method: string; notes: string }) => {
    if (selectedPayees.length === 0) return;
    try {
      const res = await settleBulk.mutateAsync({
        payees: selectedPayees,
        method,
        notes: notes || undefined,
      });
      const settled = res.data?.settled ?? 0;
      const count = res.data?.payments?.length ?? 0;
      toast.success(
        `Settled ${settled} share(s) — ${count} payout voucher${count === 1 ? '' : 's'} generated.`,
      );
      setSelectedKeys([]);
      setBulkSettleOpen(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to settle payouts.',
      );
    }
  };

  const handleBulkRemove = async () => {
    if (selectedPayees.length === 0) return;
    try {
      const res = await removeOutstanding.mutateAsync({ payees: selectedPayees });
      const trashed = res.data?.trashed?.length ?? 0;
      const skipped = res.data?.skipped?.length ?? 0;
      toast.success(
        `Moved ${trashed} outstanding item(s) to trash${skipped ? ` · ${skipped} settled item(s) skipped` : ''}.`,
      );
      setSelectedKeys([]);
      setConfirmRemove(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || err?.message || 'Failed to remove outstanding payables.',
      );
    }
  };

  const filters = useMemo(() => {
    const perType: { key: TypeFilter; label: string; icon: React.ElementType }[] = [
      { key: 'DOCTOR', label: 'Doctors', icon: Users },
      { key: 'REFERRAL', label: 'Referral partners', icon: Handshake },
    ];
    const allowed = perType.filter((f) => (f.key === 'DOCTOR' ? canDoctors : canReferrals));
    return bothPayees
      ? [{ key: 'ALL' as TypeFilter, label: 'All payees', icon: Wallet }, ...allowed]
      : allowed;
  }, [canDoctors, canReferrals, bothPayees]);

  return (
    <div className="space-y-4">
      {/* KPI cards — hospital-wide, independent of paging */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Revenue base" value={money(totals?.totalAmount)} tone="text" />
        <Kpi label="Payee share" value={money(totals?.share)} tone="text" />
        <Kpi label="Outstanding" value={money(totals?.outstandingShare)} tone="primary" />
        <Kpi label="Settled" value={money(totals?.settledShare)} tone="success" />
      </div>

      {/* Controls: type is a filter, not a page */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 bg-surface border border-border rounded-lg p-1">
          {filters.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setTypeFilter(f.key)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${
                  typeFilter === f.key ? 'bg-primary text-white' : 'text-text-muted hover:text-text'
                }`}
              >
                <Icon size={14} />
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by payee name, code or mobile…"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {/* Bulk actions — act on the selected payees' outstanding shares */}
      {someSelected && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selectedPayees.length}</span> payee(s) selected ·
            outstanding{' '}
            <span className="font-semibold text-primary">{money(selectedOutstanding)}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkSettleOpen(true)}
              disabled={settleBulk.isPending || removeOutstanding.isPending}
              className="px-3 py-1.5 bg-success text-white rounded-lg hover:bg-success/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              <CheckCheck size={14} />
              Settle selected
            </button>
            {canRemove && (
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                disabled={settleBulk.isPending || removeOutstanding.isPending}
                className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
              >
                <Trash2 size={14} />
                Remove
              </button>
            )}
            <button
              type="button"
              onClick={() => setSelectedKeys([])}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors flex items-center gap-1.5"
            >
              <X size={14} />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Payee ledger */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-background/50 text-left text-xs uppercase text-text-muted">
                <th className="px-4 py-3 w-10">
                  <input
                    ref={headerRef}
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={selectableRows.length === 0}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                    title="Select all settleable payees on this page"
                  />
                </th>
                <th className="px-4 py-3 font-medium">Payee</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium text-right">Revenue Base</th>
                <th className="px-4 py-3 font-medium text-right">Share</th>
                <th className="px-4 py-3 font-medium text-right">Outstanding</th>
                <th className="px-4 py-3 font-medium text-right">Settled</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {isLoading && rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center">
                    <Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" />
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-danger">
                    <AlertCircle size={18} className="inline mr-2" />
                    Failed to load outstanding payables.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-text-muted">
                    Nothing outstanding. Accrued shares appear here once a receipt
                    is completed.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const key = rowKey(row);
                  const canSettle = isSelectable(row);
                  const checked = selectedKeys.includes(key);
                  return (
                    <tr
                      key={key}
                      className={`hover:bg-background/60 transition-colors ${
                        checked ? 'bg-primary/5' : ''
                      }`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOne(row)}
                          disabled={!canSettle}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default disabled:opacity-40"
                          title={canSettle ? 'Select payee' : 'Nothing outstanding to select'}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => setDetails(row)}
                          title="View payee details"
                          className="group text-left"
                        >
                          <span className="font-medium text-primary transition-colors group-hover:underline">
                            {row.payeeName}
                          </span>
                          {row.payeeCode && (
                            <div className="text-xs text-text-muted">{row.payeeCode}</div>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <PayeeTypeBadge payeeType={row.payeeType} />
                      </td>
                      <td className="px-4 py-3">
                        <PayoutStatusBadge status={payoutStatusOf(row)} />
                      </td>
                      <td className="px-4 py-3 text-right text-text-muted">
                        {money(row.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-right text-text">{money(row.share)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-primary">
                        {money(row.outstandingShare)}
                      </td>
                      <td className="px-4 py-3 text-right text-success">
                        {money(row.settledShare)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canSettle ? (
                          <button
                            type="button"
                            onClick={() => setSettling(row)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success text-white text-xs font-medium hover:opacity-90 transition-opacity"
                          >
                            <CheckCircle size={13} />
                            Settle
                          </button>
                        ) : (
                          <span className="text-xs text-text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {data?.meta && (
          <Pagination
            page={data.meta.page}
            totalPages={data.meta.totalPages}
            total={data.meta.total}
            limit={data.meta.limit}
            onPageChange={setPage}
            itemName="payees"
          />
        )}
      </div>

      {settling && (
        <SettleModal row={settling} onClose={() => setSettling(null)} />
      )}

      {details && (
        <PayeeDetailsModal
          row={details}
          onClose={() => setDetails(null)}
          onSettle={(row) => {
            setDetails(null);
            setSettling(row);
          }}
        />
      )}

      {bulkSettleOpen && (
        <BulkSettleModal
          rows={selectedRows}
          pending={settleBulk.isPending}
          onClose={() => setBulkSettleOpen(false)}
          onSubmit={handleBulkSettle}
        />
      )}

      <ConfirmDialog
        open={confirmRemove}
        title="Remove outstanding payables?"
        description={`${selectedPayees.length} payee(s) with ${money(
          selectedOutstanding,
        )} outstanding will be moved to Trash. You can restore them from the Trash tab.`}
        confirmLabel="Move to Trash"
        variant="danger"
        loading={removeOutstanding.isPending}
        onConfirm={handleBulkRemove}
        onCancel={() => setConfirmRemove(false)}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: 'text' | 'primary' | 'success';
}) {
  const toneClass =
    tone === 'primary' ? 'text-primary' : tone === 'success' ? 'text-success' : 'text-text';
  return (
    <div className="bg-surface border border-border rounded-xl p-4">
      <p className="text-xs uppercase text-text-muted">{label}</p>
      <p className={`mt-1 text-lg font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared payout dialog primitives                                            */
/* -------------------------------------------------------------------------- */

/** The payment methods a payout can be settled through, with an icon each. */
const PAYMENT_METHODS: { value: string; label: string; icon: React.ElementType }[] = [
  { value: 'CASH', label: 'Cash', icon: Banknote },
  { value: 'BANK', label: 'Bank', icon: Landmark },
  { value: 'BKASH', label: 'bKash', icon: Wallet },
  { value: 'NAGAD', label: 'Nagad', icon: Smartphone },
  { value: 'CHEQUE', label: 'Cheque', icon: ScrollText },
];

const methodLabel = (value?: string) =>
  PAYMENT_METHODS.find((m) => m.value === value)?.label || value || '—';

/**
 * Overlay + panel shell shared by every payout dialog. Handles backdrop
 * dismissal and the Escape key, and locks dismissal while a request is running.
 */
function PayoutDialog({
  children,
  onClose,
  labelledBy,
  disabled = false,
  maxWidth = 'max-w-md',
}: {
  children: React.ReactNode;
  onClose: () => void;
  labelledBy: string;
  disabled?: boolean;
  maxWidth?: string;
}) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !disabled) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, disabled]);

  return (
    <div
      className="modal-overlay-enter fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
      onClick={() => {
        if (!disabled) onClose();
      }}
    >
      <div
        className={cn(
          'modal-panel-enter flex max-h-[92vh] w-full flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl',
          maxWidth,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Gradient-tinted header with an icon chip, title, optional subtitle and close. */
function DialogHeader({
  icon,
  title,
  subtitle,
  titleId,
  onClose,
  tone = 'primary',
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: React.ReactNode;
  titleId: string;
  onClose: () => void;
  tone?: 'primary' | 'success';
}) {
  const chip = tone === 'success' ? 'bg-success/10 text-success' : 'bg-primary/10 text-primary';
  const glow =
    tone === 'success' ? 'from-success/10 via-success/5' : 'from-primary/10 via-primary/5';
  return (
    <div className="relative shrink-0 border-b border-border">
      <div
        className={cn(
          'pointer-events-none absolute inset-0 bg-gradient-to-br to-transparent',
          glow,
        )}
      />
      <div className="relative flex items-start gap-3 p-4">
        <div className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl', chip)}>
          {icon}
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <h2 id={titleId} className="truncate text-base font-semibold text-text">
            {title}
          </h2>
          {subtitle && <div className="mt-0.5 text-xs text-text-muted">{subtitle}</div>}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="-mr-1 -mt-1 shrink-0 rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}

function DialogFooter({ children }: { children: React.ReactNode }) {
  return <div className="shrink-0 border-t border-border bg-background/50 px-4 py-3">{children}</div>;
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="flex items-start gap-2.5 rounded-xl border border-danger/20 bg-danger/10 p-3 text-sm text-danger">
      <AlertCircle size={16} className="mt-0.5 shrink-0" />
      <span>{message}</span>
    </div>
  );
}

/** Icon-grid payment method selector — clearer and faster than a `<select>`. */
function MethodPicker({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-medium text-text-muted">Payment method</label>
      <div className="grid grid-cols-5 gap-2">
        {PAYMENT_METHODS.map((m) => {
          const Icon = m.icon;
          const active = value === m.value;
          return (
            <button
              key={m.value}
              type="button"
              onClick={() => onChange(m.value)}
              aria-pressed={active}
              className={cn(
                'flex flex-col items-center gap-1.5 rounded-xl border px-1 py-2.5 text-[11px] font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                active
                  ? 'border-primary bg-primary/10 text-primary shadow-sm'
                  : 'border-border bg-background text-text-muted hover:border-primary/40 hover:text-text',
              )}
            >
              <Icon size={17} />
              {m.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function NotesField({
  value,
  onChange,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  id: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="mb-2 block text-xs font-medium text-text-muted">
        Notes <span className="font-normal text-text-muted/70">(optional)</span>
      </label>
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        placeholder="Add a reference, cheque number, or remark…"
        className="w-full resize-none rounded-xl border border-border bg-background px-3 py-2.5 text-sm text-text transition-shadow placeholder:text-text-muted/60 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
      />
    </div>
  );
}

/** Prominent outstanding-amount block used at the top of the settle dialogs. */
function AmountSummary({ row }: { row: PayeeOutstandingRow }) {
  return (
    <div className="rounded-2xl border border-border bg-background/60 p-4">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Outstanding to settle
          </p>
          <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-primary">
            {money(row.outstandingShare)}
          </p>
        </div>
        <PayeeTypeBadge payeeType={row.payeeType} />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-dashed border-border pt-3 text-xs text-text-muted">
        <span>Total share {money(row.share)}</span>
        <span>Already settled {money(row.settledShare)}</span>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone = 'text',
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  tone?: 'text' | 'primary' | 'success';
}) {
  const toneClass = tone === 'primary' ? 'text-primary' : tone === 'success' ? 'text-success' : 'text-text';
  return (
    <div className="rounded-xl border border-border bg-background/60 p-3">
      <div className="flex items-center gap-1.5 text-xs text-text-muted">
        <Icon size={13} />
        {label}
      </div>
      <p className={cn('mt-1.5 text-lg font-bold tabular-nums', toneClass)}>{value}</p>
    </div>
  );
}

/** Pending / posted / settled split rendered as a stacked bar plus a legend. */
function ShareSplit({ row }: { row: PayeeOutstandingRow }) {
  const total = Math.max(row.share, row.pendingShare + row.postedShare + row.settledShare, 1);
  const segments = [
    { key: 'pending', label: 'Pending', value: row.pendingShare, bar: 'bg-warning', dot: 'bg-warning' },
    { key: 'posted', label: 'Posted', value: row.postedShare, bar: 'bg-primary', dot: 'bg-primary' },
    { key: 'settled', label: 'Settled', value: row.settledShare, bar: 'bg-success', dot: 'bg-success' },
  ];
  return (
    <div className="rounded-2xl border border-border bg-background/60 p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-muted">
        Share breakdown
      </p>
      <div className="flex h-2 overflow-hidden rounded-full bg-border">
        {segments.map((s) => (
          <div
            key={s.key}
            className={s.bar}
            style={{ width: `${(s.value / total) * 100}%` }}
            title={`${s.label}: ${money(s.value)}`}
          />
        ))}
      </div>
      <div className="mt-3 grid grid-cols-3 gap-2">
        {segments.map((s) => (
          <div key={s.key} className="space-y-0.5">
            <div className="flex items-center gap-1.5 text-xs text-text-muted">
              <span className={cn('h-2 w-2 rounded-full', s.dot)} />
              {s.label}
            </div>
            <p className="text-sm font-medium tabular-nums text-text">{money(s.value)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Most recent vouchers for one payee, loaded only while the details modal is open. */
function PayeeVoucherHistory({
  payeeType,
  payeeId,
}: {
  payeeType: PayeeKind;
  payeeId: string;
}) {
  const { data, isLoading } = usePayoutVouchers({ payeeType, payeeId, limit: 5 });
  const vouchers = data?.data ?? [];

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-text-muted">
          <History size={13} />
          Recent vouchers
        </h3>
        {vouchers.length > 0 && (
          <span className="text-xs text-text-muted">
            {data?.meta?.total ?? vouchers.length} total
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="flex items-center justify-center rounded-xl border border-border bg-background/60 py-6">
          <Loader2 size={18} className="animate-spin text-primary" />
        </div>
      ) : vouchers.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-background/60 px-4 py-6 text-center text-xs text-text-muted">
          No vouchers issued yet.
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background/60">
          {vouchers.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0">
                <p className="truncate font-medium text-text">{v.paymentNumber}</p>
                <p className="truncate text-xs text-text-muted">
                  {new Date(v.paidAt).toLocaleDateString()}
                  {v.method ? ` · ${methodLabel(v.method)}` : ''}
                </p>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-text">
                {money(v.amount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Tone map for one accrual line's settlement status. */
const ACCRUAL_STATUS_CLASS: Record<string, string> = {
  PENDING: 'bg-warning/10 text-warning',
  POSTED: 'bg-primary/10 text-primary',
  SETTLED: 'bg-success/10 text-success',
};

const ACCRUAL_PAGE_SIZE = 6;
/** Accruals loaded up front so one receipt is never split across pages. */
const ACCRUAL_FETCH_LIMIT = 1000;

/**
 * One row in the history trail. Diagnostic accruals are rolled up per OPD
 * receipt (one receipt = one row, share summed) so the payee is never shown a
 * commission test by test — the row reads "OPD Receipt" + its receipt number.
 */
type AccrualRow = {
  key: string;
  kind: 'consultation' | 'admission' | 'diagnostic';
  title: string;
  when?: string | null;
  share: number;
  status: string;
  /** Vouchers this row was paid through; more than one means mixed. */
  paymentNumbers: Set<string>;
  appointment?: RevenueDistribution['appointment'];
  admission?: RevenueDistribution['admission'];
  receipt?: DiagnosticReceipt;
  testCount?: number;
};

/**
 * The accrual trail behind one payee's balance — the completed receipts and
 * consultations that accrued their share, newest first. "Outstanding" (the
 * default) shows only the unpaid lines that make up the Settle amount; "All"
 * adds the already-paid ones so the full history is visible. Rendered inside
 * the dedicated History modal.
 */
function PayeeAccrualHistory({
  payeeType,
  payeeId,
  outstanding,
}: {
  payeeType: PayeeKind;
  payeeId: string;
  /** The payee's current outstanding total, shown as a sanity-check header. */
  outstanding: number;
}) {
  const [scope, setScope] = useState<'outstanding' | 'all'>('outstanding');
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [scope, payeeId]);

  // Load the whole trail in one go, then group and page locally — a receipt's
  // test lines must stay together so its share is never shown in fragments.
  const { data, isLoading } = usePayeeAccruals({
    payeeType,
    payeeId,
    page: 1,
    limit: ACCRUAL_FETCH_LIMIT,
    outstandingOnly: scope === 'outstanding',
  });

  const accruals = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  const rows = useMemo<AccrualRow[]>(() => {
    const out: AccrualRow[] = [];
    const diagnosticIndex = new Map<string, number>();

    for (const d of accruals) {
      const share = Number(d.doctorShare) || 0;
      const paymentNumbers = new Set<string>(
        d.doctorPayment?.paymentNumber ? [d.doctorPayment.paymentNumber] : [],
      );

      if (d.sourceType === 'CONSULTATION') {
        out.push({
          key: d.id,
          kind: 'consultation',
          title: d.appointment?.appointmentType || 'Consultation',
          when: d.appointment?.date || d.createdAt,
          share,
          status: d.status,
          paymentNumbers,
          appointment: d.appointment,
        });
        continue;
      }

      if (d.sourceType === 'ADMISSION') {
        out.push({
          key: d.id,
          kind: 'admission',
          title: `IPD admission${
            d.admission?.patient
              ? ` — ${[d.admission.patient.firstName, d.admission.patient.lastName]
                  .filter(Boolean)
                  .join(' ')}`
              : ''
          }`,
          when: d.admission?.admittedAt || d.createdAt,
          share,
          status: d.status,
          paymentNumbers,
          admission: d.admission,
        });
        continue;
      }

      const receipt = d.receiptItem?.receipt;
      const groupKey = receipt?.id || d.receiptItem?.receiptId || d.id;
      const existing = diagnosticIndex.get(groupKey);
      if (existing !== undefined) {
        const row = out[existing];
        row.share += share;
        row.testCount = (row.testCount || 1) + 1;
        // A receipt paid in one settlement is all-or-nothing; if a stray line
        // is still unpaid, surface that state rather than a false "SETTLED".
        if (row.status === 'SETTLED' && d.status !== 'SETTLED') row.status = d.status;
        paymentNumbers.forEach((n) => row.paymentNumbers.add(n));
        continue;
      }

      diagnosticIndex.set(groupKey, out.length);
      out.push({
        key: `receipt:${groupKey}`,
        kind: 'diagnostic',
        title: receipt ? 'OPD Receipt' : 'Diagnostic Receipt',
        when: receipt?.createdAt || d.createdAt,
        share,
        status: d.status,
        paymentNumbers,
        receipt,
        testCount: 1,
      });
    }

    return out;
  }, [accruals]);

  const totalPages = Math.max(1, Math.ceil(rows.length / ACCRUAL_PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * ACCRUAL_PAGE_SIZE, page * ACCRUAL_PAGE_SIZE);

  // Keep the local page in range when a refetch shrinks the trail.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-text-muted">
          {scope === 'outstanding' ? (
            <>
              Unpaid accruals totalling{' '}
              <span className="font-medium text-primary">{money(outstanding)}</span>
            </>
          ) : (
            'Every accrual, paid or unpaid'
          )}
          {rows.length > 0 ? ` · ${rows.length} item(s)` : ''}
          {typeof meta?.total === 'number' && meta.total > accruals.length
            ? ` · showing latest ${accruals.length} of ${meta.total}`
            : ''}
        </p>
        <div className="flex items-center gap-1 rounded-lg border border-border bg-background p-0.5">
          {(['outstanding', 'all'] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setScope(key)}
              className={cn(
                'rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
                scope === key ? 'bg-primary text-white' : 'text-text-muted hover:text-text',
              )}
            >
              {key === 'outstanding' ? 'Outstanding' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-xl border border-border bg-background/60 py-6">
          <Loader2 size={18} className="animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-background/60 px-4 py-6 text-center text-xs text-text-muted">
          {scope === 'outstanding'
            ? 'Nothing outstanding — every accrual has been settled.'
            : 'No accruals recorded for this payee yet.'}
        </div>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border bg-background/60">
          {pageRows.map((row) => {
            const paymentNumber =
              row.paymentNumbers.size === 1 ? [...row.paymentNumbers][0] : undefined;
            return (
              <li key={row.key} className="flex items-start gap-3 px-3 py-2.5">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                  <ReceiptText size={14} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-text" title={row.title}>
                    {row.title}
                  </p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-text-muted">
                    <SourceBadge
                      source={
                        row.kind === 'consultation'
                          ? 'CONSULTATION'
                          : row.kind === 'admission'
                            ? 'ADMISSION'
                            : 'DIAGNOSTIC'
                      }
                    />
                    {row.kind === 'consultation' ? (
                      <span className="truncate">{row.appointment?.patientName || '—'}</span>
                    ) : row.kind === 'admission' ? (
                      <span className="truncate">
                        {row.admission?.admittedAt
                          ? new Date(row.admission.admittedAt).toLocaleDateString()
                          : '—'}
                      </span>
                    ) : row.receipt ? (
                      <Link
                        to={`/opd/diagnostic-receipts/${row.receipt.id}`}
                        className="font-mono text-primary hover:underline"
                      >
                        {row.receipt.receiptNumber}
                      </Link>
                    ) : (
                      <span>—</span>
                    )}
                    {row.kind === 'diagnostic' && (row.testCount || 0) > 1 && (
                      <span>{row.testCount} test(s)</span>
                    )}
                    <span>{row.when ? new Date(row.when).toLocaleDateString() : '—'}</span>
                  </p>
                  {row.status === 'SETTLED' && paymentNumber && (
                    <p className="mt-0.5 text-[11px] text-text-muted">
                      Paid via <span className="font-mono text-text">{paymentNumber}</span>
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold tabular-nums text-text">{money(row.share)}</p>
                  <span
                    className={cn(
                      'mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium',
                      ACCRUAL_STATUS_CLASS[row.status] || 'bg-background text-text-muted',
                    )}
                  >
                    {row.status}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {totalPages > 1 && (
        <div className="mt-2 flex items-center justify-between text-[11px] text-text-muted">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-lg border border-border p-1 transition-colors hover:bg-background disabled:opacity-40"
              title="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-lg border border-border p-1 transition-colors hover:bg-background disabled:opacity-40"
              title="Next page"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Dedicated "where does this come from" sheet — the receipt-by-receipt accrual
 * trail behind one payee's balance, opened from the payee profile modal. Kept
 * separate so the profile stays compact while the full history is one click
 * away.
 */
function PayeeHistoryModal({
  row,
  onClose,
}: {
  row: PayeeOutstandingRow;
  onClose: () => void;
}) {
  return (
    <PayoutDialog onClose={onClose} labelledBy="payee-history-title" maxWidth="max-w-2xl">
      <DialogHeader
        titleId="payee-history-title"
        icon={<History size={19} />}
        title="History"
        subtitle={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-medium text-text">{row.payeeName}</span>
            <PayeeTypeBadge payeeType={row.payeeType} />
            {row.payeeCode && (
              <span className="inline-flex items-center gap-1">
                <Hash size={11} />
                {row.payeeCode}
              </span>
            )}
          </span>
        }
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-4">
        {row.payeeId ? (
          <PayeeAccrualHistory
            payeeType={row.payeeType}
            payeeId={row.payeeId}
            outstanding={row.outstandingShare}
          />
        ) : (
          <div className="rounded-xl border border-dashed border-border bg-background/60 px-4 py-6 text-center text-sm text-text-muted">
            This payee has no linked record, so there is nothing to trace.
          </div>
        )}
      </div>

      <DialogFooter>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-text-muted">
            <span>
              Outstanding{' '}
              <span className="font-semibold text-primary">{money(row.outstandingShare)}</span>
            </span>
            <span>
              Settled <span className="font-semibold text-success">{money(row.settledShare)}</span>
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
          >
            Close
          </button>
        </div>
      </DialogFooter>
    </PayoutDialog>
  );
}

/** Payee profile sheet opened by clicking a payee's name in the ledger. */
function PayeeDetailsModal({
  row,
  onClose,
  onSettle,
}: {
  row: PayeeOutstandingRow;
  onClose: () => void;
  onSettle: (row: PayeeOutstandingRow) => void;
}) {
  const isReferral = row.payeeType === 'REFERRAL';
  const canSettle = !!row.payeeId && row.outstandingShare > 0;
  const [showHistory, setShowHistory] = useState(false);

  return (
    <>
      <PayoutDialog
        onClose={onClose}
        disabled={showHistory}
        labelledBy="payee-details-title"
        maxWidth="max-w-lg"
      >
        <DialogHeader
          titleId="payee-details-title"
          icon={isReferral ? <Handshake size={19} /> : <Users size={19} />}
          title={row.payeeName}
          subtitle={
            <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {row.payeeCode && (
                <span className="inline-flex items-center gap-1">
                  <Hash size={11} />
                  {row.payeeCode}
                </span>
              )}
              {row.mobileNumber && (
                <span className="inline-flex items-center gap-1">
                  <Phone size={11} />
                  {row.mobileNumber}
                </span>
              )}
              <PayeeTypeBadge payeeType={row.payeeType} />
            </span>
          }
          onClose={onClose}
        />

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            <StatCard icon={Wallet} label="Outstanding" value={money(row.outstandingShare)} tone="primary" />
            <StatCard icon={CheckCircle} label="Settled" value={money(row.settledShare)} tone="success" />
            <StatCard icon={TrendingUp} label="Revenue base" value={money(row.totalAmount)} />
            <StatCard icon={Percent} label="Payee share" value={money(row.share)} />
          </div>

          <ShareSplit row={row} />

          {row.payeeId && <PayeeVoucherHistory payeeType={row.payeeType} payeeId={row.payeeId} />}
        </div>

        <DialogFooter>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {row.payeeId && (
                <button
                  type="button"
                  onClick={() => setShowHistory(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
                >
                  <History size={14} />
                  History
                </button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text"
              >
                Close
              </button>
              {canSettle && (
                <button
                  type="button"
                  onClick={() => onSettle(row)}
                  className="inline-flex items-center gap-2 rounded-lg bg-success px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-success/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-success"
                >
                  <CheckCircle size={16} />
                  Settle {money(row.outstandingShare)}
                </button>
              )}
            </div>
          </div>
        </DialogFooter>
      </PayoutDialog>

      {showHistory && row.payeeId && (
        <PayeeHistoryModal row={row} onClose={() => setShowHistory(false)} />
      )}
    </>
  );
}

function SettleModal({
  row,
  onClose,
}: {
  row: PayeeOutstandingRow;
  onClose: () => void;
}) {
  const [method, setMethod] = useState('CASH');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const settle = useSettlePayouts();
  const isReferral = row.payeeType === 'REFERRAL';

  const handleSettle = async () => {
    if (!row.payeeId) {
      setError('This payee cannot be settled (no linked record).');
      return;
    }
    setError(null);
    try {
      const res = await settle.mutateAsync({
        payeeType: row.payeeType,
        payeeId: row.payeeId,
        method,
        notes: notes || undefined,
      });
      const count = res.data?.payments?.length ?? 0;
      toast.success(`Settled — ${count} payout voucher${count === 1 ? '' : 's'} generated.`);
      onClose();
    } catch (err: any) {
      setError(
        err?.response?.data?.message || err?.message || 'Failed to settle payout.',
      );
    }
  };

  return (
    <PayoutDialog onClose={onClose} disabled={settle.isPending} labelledBy="settle-modal-title">
      <DialogHeader
        titleId="settle-modal-title"
        icon={isReferral ? <Handshake size={19} /> : <Users size={19} />}
        title="Settle payout"
        subtitle={
          <>
            Issue a payout voucher for{' '}
            <span className="font-medium text-text">{row.payeeName}</span>
          </>
        }
        onClose={onClose}
      />

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <AmountSummary row={row} />
        {error && <ErrorBanner message={error} />}
        <MethodPicker value={method} onChange={setMethod} />
        <NotesField id="settle-notes" value={notes} onChange={setNotes} />
      </div>

      <DialogFooter>
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-xs text-text-muted">
            <ShieldCheck size={14} className="shrink-0 text-success" />
            A numbered voucher is generated and logged.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={settle.isPending}
              className="shrink-0 rounded-lg px-4 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSettle}
              disabled={settle.isPending}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
            >
              {settle.isPending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCircle size={16} />
              )}
              Settle &amp; create voucher
            </button>
          </div>
        </div>
      </DialogFooter>
    </PayoutDialog>
  );
}

function BulkSettleModal({
  rows,
  pending,
  onClose,
  onSubmit,
}: {
  rows: PayeeOutstandingRow[];
  pending: boolean;
  onClose: () => void;
  onSubmit: (opts: { method: string; notes: string }) => void;
}) {
  const [method, setMethod] = useState('CASH');
  const [notes, setNotes] = useState('');

  const total = rows.reduce((sum, row) => sum + row.outstandingShare, 0);
  const doctors = rows.filter((r) => r.payeeType === 'DOCTOR').length;
  const referrals = rows.length - doctors;

  return (
    <PayoutDialog onClose={onClose} disabled={pending} labelledBy="bulk-settle-title">
      <DialogHeader
        titleId="bulk-settle-title"
        tone="success"
        icon={<CheckCheck size={19} />}
        title={`Settle ${rows.length} payee${rows.length === 1 ? '' : 's'}`}
        subtitle="One numbered voucher will be issued per payee."
        onClose={onClose}
      />

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <div className="rounded-2xl border border-border bg-background/60 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-text-muted">
            Total outstanding
          </p>
          <p className="mt-1 text-3xl font-bold tabular-nums tracking-tight text-success">
            {money(total)}
          </p>
          <div className="mt-3 flex items-center justify-between border-t border-dashed border-border pt-3 text-xs text-text-muted">
            <span className="inline-flex items-center gap-1.5">
              <Users size={12} />
              {rows.length} payee{rows.length === 1 ? '' : 's'}
            </span>
            <span>
              {doctors} doctor{doctors === 1 ? '' : 's'}
              {referrals > 0 ? ` · ${referrals} referral${referrals === 1 ? '' : 's'}` : ''}
            </span>
          </div>
        </div>

        <MethodPicker value={method} onChange={setMethod} />
        <NotesField id="bulk-settle-notes" value={notes} onChange={setNotes} />
      </div>

      <DialogFooter>
        <div className="space-y-3">
          <p className="flex items-center gap-1.5 text-xs text-text-muted">
            <ShieldCheck size={14} className="shrink-0 text-success" />
            {rows.length} voucher{rows.length === 1 ? '' : 's'} will be generated.
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={pending}
              className="shrink-0 rounded-lg px-4 py-2.5 text-sm font-medium text-text-muted transition-colors hover:bg-background hover:text-text disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => onSubmit({ method, notes })}
              disabled={pending || rows.length === 0}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-success px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-success/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-success disabled:opacity-50"
            >
              {pending ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <CheckCheck size={16} />
              )}
              Settle all &amp; create vouchers
            </button>
          </div>
        </div>
      </DialogFooter>
    </PayoutDialog>
  );
}
